import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { Plus, Trash } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import { generateArrayOfYears, showErrorToast, showSuccessToast } from "../../../utils";
import {
  organizationAdministrationKeys,
  useGetScopeTwoConfig,
  useUpdateScopeTwoConfig,
} from "../../../hooks/query/organization-administration";
import PageHeader from "../../../components/text/PageHeader";

const QUARTERS = ["ALL", "Q1", "Q2", "Q3", "Q4"];

// Lime focus for raw Chakra controls (project convention — never the blue ring).
const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

// Parse "2024-Q1" / "2024-ALL" into { year, quarter }.
const parsePeriodKey = (key) => {
  const idx = key.lastIndexOf("-");
  return { year: key.slice(0, idx), quarter: key.slice(idx + 1) };
};
const toPeriodKey = (year, quarter) => `${year}-${quarter}`;

function ScopeTwoEngine() {
  const { t } = useTranslation("translation", { keyPrefix: "scopeTwoEngine" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const years = useMemo(() => generateArrayOfYears(), []);

  const { data: config } = useGetScopeTwoConfig();
  const [defaultVersion, setDefaultVersion] = useState("V1");
  const [periods, setPeriods] = useState([]); // array of "year-quarter" keys
  const [newYear, setNewYear] = useState(String(new Date().getFullYear() - 1));
  const [newQuarter, setNewQuarter] = useState("ALL");

  useEffect(() => {
    if (config) {
      setDefaultVersion(config.scopeTwoVersion ?? "V1");
      setPeriods(config.scopeTwoV1Periods ?? []);
    }
  }, [config]);

  const { mutate: updateConfig, isLoading } = useUpdateScopeTwoConfig({
    onSuccess: () => {
      queryClient.invalidateQueries(organizationAdministrationKeys.scopeTwoConfig());
      showSuccessToast(toast, t("saved"));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const addPeriod = () => {
    const key = toPeriodKey(newYear, newQuarter);
    if (!periods.includes(key)) {
      setPeriods((prev) => [...prev, key]);
    }
  };

  const removePeriod = (key) => setPeriods((prev) => prev.filter((p) => p !== key));

  const save = () => updateConfig({ scopeTwoVersion: defaultVersion, scopeTwoV1Periods: periods });

  return (
    <Page>
      <Box maxW="720px">
        <PageHeader title={t("title")} subTitle={t("description")} />

        <Stack spacing={8} mt={6}>
          <Box>
            <Text fontSize="body.m" fontWeight="600" color="gray.700" mb={1}>{t("defaultLabel")}</Text>
            <Text fontSize="body.xs" color="gray.500" mb={2}>{t("defaultHint")}</Text>
            <Select
              maxW="280px"
              value={defaultVersion}
              onChange={(e) => setDefaultVersion(e.target.value)}
              {...LIME_FOCUS}
            >
              <option value="V1">{t("v1")}</option>
              <option value="V2">{t("v2")}</option>
            </Select>
          </Box>

          <Box>
            <Text fontSize="body.m" fontWeight="600" color="gray.700" mb={1}>{t("forceV1Label")}</Text>
            <Text fontSize="body.xs" color="gray.500" mb={3}>{t("forceV1Hint")}</Text>

            <HStack mb={3} align="flex-end">
              <Box>
                <Text fontSize="body.xs" color="gray.500" mb={1}>{t("year")}</Text>
                <Select size="sm" w="120px" value={newYear} onChange={(e) => setNewYear(e.target.value)} {...LIME_FOCUS}>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </Select>
              </Box>
              <Box>
                <Text fontSize="body.xs" color="gray.500" mb={1}>{t("quarter")}</Text>
                <Select size="sm" w="160px" value={newQuarter} onChange={(e) => setNewQuarter(e.target.value)} {...LIME_FOCUS}>
                  {QUARTERS.map((q) => <option key={q} value={q}>{t(`quarters.${q}`)}</option>)}
                </Select>
              </Box>
              <Button size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={addPeriod}>
                {t("add")}
              </Button>
            </HStack>

            <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
              <Table size="sm" variant="unstyled">
                <Thead>
                  <Tr bg="gray.50">
                    <Th py={2} px={4} fontSize="13px" color="gray.600" textTransform="none">{t("year")}</Th>
                    <Th py={2} px={4} fontSize="13px" color="gray.600" textTransform="none">{t("quarter")}</Th>
                    <Th py={2} px={4} width="48px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {periods.length === 0 ? (
                    <Tr><Td colSpan={3} py={4} px={4} textAlign="center" fontSize="14px" color="gray.400">{t("noRules")}</Td></Tr>
                  ) : (
                    periods.map((key) => {
                      const { year, quarter } = parsePeriodKey(key);
                      return (
                        <Tr key={key} borderTop="1px solid" borderColor="gray.100">
                          <Td py={2} px={4} fontSize="14px" color="gray.700">{year}</Td>
                          <Td py={2} px={4} fontSize="14px" color="gray.700">{t(`quarters.${quarter}`)}</Td>
                          <Td py={2} px={4} textAlign="right">
                            <IconButton
                              aria-label="Remove period rule"
                              icon={<Trash size={14} />}
                              size="sm"
                              variant="ghost"
                              color="gray.500"
                              _hover={{ color: "red.500", bg: "red.50" }}
                              onClick={() => removePeriod(key)}
                            />
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          <Flex justify="flex-end">
            <Button variant="primary" onClick={save} isLoading={isLoading}>{t("save")}</Button>
          </Flex>
        </Stack>
      </Box>
    </Page>
  );
}

export default WithAuthorization(ScopeTwoEngine);
